import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ArrowLeft, Sparkles, Check, Wand2, Image as ImageIcon, Shirt, Users, Box, Layers, FilmIcon, Scissors, Loader2 } from 'lucide-react';
import { fetchAvailableModels } from '../../services/api';
import { CREDIT_COSTS, RENDER_COST_PER_CLIP } from '../../types';
import { runPreStoryboardPipeline, countPendingGeneration } from '../../services/preStoryboardPipeline';
import { generateStoryboard, mapEntitiesToShot } from '../../services/geminiService';

// ============================================================================
// Tier definitions
// ============================================================================

const TIERS = [
    {
        id: 'economy',
        name: 'Economy',
        tagline: 'Lowest cost · Faster turnaround',
        accent: 'from-emerald-500/20 to-emerald-500/5',
        ring: 'ring-emerald-500/40',
        chip: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    },
    {
        id: 'standard',
        name: 'Standard',
        tagline: 'Balanced quality and cost',
        accent: 'from-violet-500/20 to-violet-500/5',
        ring: 'ring-violet-500/40',
        chip: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
    },
    {
        id: 'premium',
        name: 'Premium',
        tagline: 'Best-in-class output',
        accent: 'from-amber-500/20 to-amber-500/5',
        ring: 'ring-amber-500/40',
        chip: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    },
];

// ============================================================================
// Cost helpers — parses model.cost_estimate strings.
// ============================================================================

/**
 * Extract a credit count from a model entry.
 *
 * Reads structured numeric fields first (set by the admin backend per the
 * Model Catalog Cost Fields spec at `/app/docs/MODEL_CATALOG_COST_FIELDS_SPEC.md`):
 *   - `model.cost_credits`         (preferred — integer credits per call)
 *   - `model.credits_per_call`     (alias)
 *
 * Falls back to parsing the free-form `cost_estimate` string for legacy
 * catalog entries. The string parser only trusts numbers that are explicitly
 * labelled as credits (e.g. "100 credits", "≈ 25 cr"). Currency strings like
 * "$0.05/sec" are deliberately ignored — they cannot be converted to credits
 * without a ratio, and grabbing the trailing digits gave very wrong numbers
 * (5 credits for a 100-credit Veo clip). Returns `tierFallback` when the
 * string is unparseable so the user clearly sees a default rather than a
 * mis-parsed value.
 */
const parseCreditCost = (model, tierFallback) => {
    if (!model) return tierFallback;
    // 1. Structured numeric fields take precedence.
    if (Number.isFinite(model.cost_credits))    return Math.max(0, Math.round(model.cost_credits));
    if (Number.isFinite(model.credits_per_call)) return Math.max(0, Math.round(model.credits_per_call));
    // 2. Legacy string parsing.
    const estimate = model.cost_estimate;
    if (estimate == null) return tierFallback;
    const s = String(estimate).toLowerCase().trim();
    if (s === '' || /free|included|complimentary|no\s*cost/.test(s)) return 0;
    // Only accept a number that is explicitly labelled with "credit"/"cr".
    const m = s.match(/(\d+(?:\.\d+)?)\s*(?:cr\b|credits?\b)/);
    if (m) return Math.max(0, Math.round(parseFloat(m[1])));
    // Or a bare integer like "25".
    const bare = s.match(/^\s*(\d+(?:\.\d+)?)\s*$/);
    if (bare) return Math.max(0, Math.round(parseFloat(bare[1])));
    // Anything else (currency, "$0.05/sec", "Pro plan", etc.) — don't guess.
    return tierFallback;
};

// Per-capability fallback when the admin returns no cost_estimate.
const TIER_FALLBACK = { economy: 10, standard: 25, premium: 60 };

// Map "what we need to generate" → which capability key on the admin backend
// drives that operation. The keys are tried in order and the first match
// (cap key contains any of the needles) is used.
//
// Priority order matters: list the most specific keyword FIRST so a generic
// keyword (like "image") doesn't accidentally pick the wrong capability.
// Verified against admin response 2026-05-07.
// Final clip generation. Panel-based projects (`project.usesPanelImages`)
// use the `references-to-video` capability; legacy projects use
// `image-to-video` / `start-end-frame`. We expose two needle lists so the
// callers can pick the right one based on the project flag below.
//
// IMPORTANT: keep "references" / "references-to-video" as the highest
// priority needle for panel-based projects — refs-to-video has its own
// distinct backend pricing & model pool, and conflating it with
// image-to-video produces wrong cost estimates AND a wrong model_key on
// the generation request.
const VIDEO_NEEDLES_LEGACY  = ['image to video', 'start-end-frame', 'text to video'];
const VIDEO_NEEDLES_PANEL   = ['references-to-video', 'references_to_video', 'references to video', 'referencestovideo'];

const CAPABILITY_KEYS = {
    // Character portraits, costumes, element images all run through the
    // platform's image-generation capability.
    portrait:   ['image_generation', 'image generation', 'text to image'],
    costume:    ['image_generation', 'image generation', 'text to image'],
    element:    ['image_generation', 'image generation', 'text to image'],
    // Virtual try-on uses reference-image-to-image generation.
    tryon:      ['image reference to image', 'image_generation', 'text to image'],
    // Storyboard is text generation — not an image or video model.
    storyboard: ['script_from_concept', 'storyboard', 'reasoning'],
    // Frame prompts: dedicated capability if exposed, else generic prompt gen.
    framePrompt:['frame_prompts', 'video_prompt', 'prompt'],
    // Frame still images = image generation. Avoid 'frame' which collides
    // with the "start-end-frame videos" (video) capability.
    frameImage: ['image_generation', 'image generation', 'text to image'],
    // Default video needles — overridden per-project via usesPanelImages.
    video:      VIDEO_NEEDLES_LEGACY,
};

const findCapability = (capabilities, needles) => {
    const lower = capabilities.map(c => ({ ...c, _k: (c.key || '').toLowerCase(), _n: (c.name || '').toLowerCase() }));
    for (const n of needles) {
        const found = lower.find(c => c._k.includes(n) || c._n.includes(n));
        if (found) return found;
    }
    return null;
};

const pickTierModel = (capability, tierId) => {
    if (!capability) return null;
    const tierList = capability.tiers?.[tierId];
    if (Array.isArray(tierList) && tierList.length > 0) return tierList[0];
    // Fall back to capability.models[0] when this capability doesn't expose
    // tier-specific models. NOTE: This is a DISPLAY-only fallback — callers
    // that decide what model to PERSIST should use `pickTierModelStrict`
    // below so a missing tier mapping doesn't silently snap to the
    // (often premium) first model in the catalog.
    if (capability.models?.length > 0) return capability.models[0];
    return null;
};

// Strict variant — returns null when the capability has no tier-specific
// entry for `tierId`. Used by `buildSelectedModelsForTier` so we never
// silently persist a premium model into localStorage just because the
// admin hasn't onboarded an economy/standard option for that capability
// yet. The downstream resolver (videoPayloadBuilder.resolveSelectedVideoModel)
// will then fall back to the catalog default for that capability, which
// is safer than forcing a premium pick.
const pickTierModelStrict = (capability, tierId) => {
    if (!capability) return null;
    const tierList = capability.tiers?.[tierId];
    if (Array.isArray(tierList) && tierList.length > 0) return tierList[0];
    return null;
};

// Cost-based fallback. When admin hasn't onboarded tier-specific models
// for a video capability (refs-to-video, image-to-video, …), we still
// want the user's tier choice to be honoured — otherwise the legacy
// `models[0]` fallback silently produced a Premium model for Economy
// tier users (see bug report Feb 2026: Economy tier resolved to
// `seedance2-ref2video` instead of `pixverse-ref2video`).
//
// Strategy: sort `capability.models` by per-call credit cost ascending,
// then slot:
//   economy  → cheapest (index 0)
//   standard → middle   (index ⌊n/2⌋)
//   premium  → priciest (last index)
//
// Works even when the catalog only exposes 1-2 models in the pool — all
// tiers collapse to the same model, which is still correct ("nothing
// cheaper available").
const pickTierModelByCost = (capability, tierId) => {
    if (!capability) return null;
    const models = Array.isArray(capability.models) ? capability.models.slice() : [];
    if (models.length === 0) return null;
    const costOf = (m) => {
        // Try common cost fields the backend exposes. Use `??` carefully —
        // `Number(null)` is 0 (not NaN), so we have to explicitly check for
        // missing/non-finite values before coercing.
        const fields = ['credit_cost', 'creditCost', 'cost', 'price', 'cost_credits', 'credits_per_call'];
        for (const f of fields) {
            const v = m?.[f];
            if (v === null || v === undefined) continue;
            const n = Number(v);
            if (Number.isFinite(n)) return n;
        }
        return Number.POSITIVE_INFINITY;
    };
    models.sort((a, b) => costOf(a) - costOf(b));
    const lastIdx = models.length - 1;
    let idx;
    if (tierId === 'economy')      idx = 0;
    else if (tierId === 'premium') idx = lastIdx;
    else                            idx = Math.floor(lastIdx / 2);
    return models[idx] || null;
};

// Priority-based fallback (Feb 13 2026). Used for non-video capabilities
// (portraits, elements, storyboard, prompts, frames, ...) that lack any
// tier mapping. Picks the top-priority model from `capability.models`.
//
// If any model exposes a numeric `priority` (or `rank`, `order`) field,
// we sort ascending by that so priority=1 wins over priority=2. If no
// priority field exists we assume the admin catalog is already ordered
// (index 0 = top priority).
//
// This replaces the previous behaviour of silently skipping the capability
// which then caused runtime "No selected model found" warnings for image
// generation, character portraits, and element props.
const pickTierModelByPriority = (capability) => {
    if (!capability) return null;
    const models = Array.isArray(capability.models) ? capability.models.slice() : [];
    if (models.length === 0) return null;
    const priorityOf = (m) => {
        const fields = ['priority', 'rank', 'order', 'sort_order'];
        for (const f of fields) {
            const v = m?.[f];
            if (v === null || v === undefined) continue;
            const n = Number(v);
            if (Number.isFinite(n)) return n;
        }
        return Number.POSITIVE_INFINITY; // unranked models sink to the end
    };
    // Stable sort by priority; ties preserve catalog order.
    models.sort((a, b) => priorityOf(a) - priorityOf(b));
    return models[0] || null;
};

const costFor = (capability, tierId) => {
    const m = pickTierModel(capability, tierId);
    const fallback = TIER_FALLBACK[tierId];
    const value = parseCreditCost(m, fallback);
    // The cost is "trustworthy" only when it came from a structured numeric
    // field on the model. Anything else (string parse, fallback) is flagged.
    const trusted = !!m && (Number.isFinite(m.cost_credits) || Number.isFinite(m.credits_per_call));
    return { value, trusted };
};

// ============================================================================
// Try-on confirmation modal — used during the pipeline run.
// ============================================================================

const TryOnConfirmModal = ({ characters, onConfirm, onSkip }) => {
    const [selected, setSelected] = useState(() => new Set(characters.map(c => c.id)));
    const toggle = (id) => {
        setSelected(prev => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="tier-tryon-modal">
            <div className="bg-zinc-950 border border-white/10 rounded-2xl max-w-2xl w-full p-6 shadow-2xl">
                <h3 className="text-lg font-bold text-white mb-1">Run virtual try-on?</h3>
                <p className="text-sm text-zinc-400 mb-5">Combine each character's portrait with their costume to produce a full reference shot. Pick the characters to include or skip the step.</p>
                <div className="space-y-2 mb-5 max-h-72 overflow-y-auto">
                    {characters.map(c => (
                        <label key={c.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 cursor-pointer" data-testid={`tier-tryon-row-${c.id}`}>
                            <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} className="w-4 h-4 accent-violet-500" />
                            <div className="w-10 h-10 rounded-md bg-white/5 overflow-hidden flex-shrink-0">
                                {c.image && <img src={c.image} alt="" className="w-full h-full object-cover" />}
                            </div>
                            <div className="text-sm text-white">{c.name}</div>
                        </label>
                    ))}
                </div>
                <div className="flex gap-3">
                    <button onClick={onSkip} className="flex-1 px-4 py-2.5 rounded-xl border border-white/15 text-sm text-zinc-300 hover:bg-white/5" data-testid="tier-tryon-skip-btn">
                        Skip try-on
                    </button>
                    <button onClick={() => onConfirm(Array.from(selected))} className="flex-1 px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold" data-testid="tier-tryon-confirm-btn">
                        Run for selected ({selected.size})
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// Main TierSelection component
// ============================================================================

export const TierSelection = ({
    initialData,
    onSubmit,
    onBack,
    onTrackCreditsUsed,
    onRefreshCredits,
    onSaveMedia,
}) => {
    const [capabilities, setCapabilities] = useState([]);
    const [loadingCaps, setLoadingCaps] = useState(true);
    const [capsError, setCapsError] = useState(null);
    const [selectedTier, setSelectedTier] = useState(initialData.tier || 'standard');

    // Pipeline run state
    const [running, setRunning] = useState(false);
    const [stage, setStage] = useState('idle');           // 'gen' | 'storyboard'
    const [progress, setProgress] = useState(null);       // { current, total, name, step }
    const [tryOnPending, setTryOnPending] = useState(null); // { characters, resolve }
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoadingCaps(true); setCapsError(null);
                const caps = await fetchAvailableModels();
                if (!cancelled) setCapabilities(caps || []);
            } catch (e) {
                if (!cancelled) setCapsError(e.message || 'Failed to load model catalog.');
            } finally {
                if (!cancelled) setLoadingCaps(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // ---------- Pending counts (drives the cost rows) ----------
    const pending = useMemo(
        () => countPendingGeneration(initialData, initialData.characters || []),
        [initialData]
    );

    // Plan how many shots / scenes the storyboard will need.
    const plannedScenes = pending.sceneCount || Math.max(2, Math.round(pending.plannedShots / 3));
    const plannedShots = pending.plannedShots;
    const branding = initialData.branding || {};
    const logoEnabled = !!(branding.logo?.enabled && branding.logo?.src);
    const outroEnabled = !!(branding.outro?.enabled && (branding.outro.imageSrc || branding.outro.text));

    // Panel-based projects (`usesPanelImages: true` — the default for all
    // new projects since Feb 2026) use the `references-to-video` capability
    // at video-generation time, NOT image-to-video. The breakdown UI must
    // reflect that so the user's estimated total + "Models used at this
    // tier" panel show the correct refs-to-video model & cost.
    const usesPanels = !!initialData.usesPanelImages;
    const videoNeedles = usesPanels ? VIDEO_NEEDLES_PANEL : VIDEO_NEEDLES_LEGACY;

    // ---------- Cost computation per tier ----------
    const tierBreakdowns = useMemo(() => {
        const caps = capabilities;
        const out = {};
        for (const tier of TIERS) {
            const tid = tier.id;
            const portraitCap = findCapability(caps, CAPABILITY_KEYS.portrait);
            const costumeCap  = findCapability(caps, CAPABILITY_KEYS.costume);
            const elementCap  = findCapability(caps, CAPABILITY_KEYS.element);
            const tryOnCap    = findCapability(caps, CAPABILITY_KEYS.tryon);
            const storyboardCap = findCapability(caps, CAPABILITY_KEYS.storyboard);
            const promptCap   = findCapability(caps, CAPABILITY_KEYS.framePrompt);
            const frameCap    = findCapability(caps, CAPABILITY_KEYS.frameImage);
            const videoCap    = findCapability(caps, videoNeedles);

            const portraitCost  = costFor(portraitCap, tid);
            const costumeCost   = costFor(costumeCap, tid);
            const elementCost   = costFor(elementCap, tid);
            const tryOnCost     = costFor(tryOnCap, tid);
            const storyboardCost = costFor(storyboardCap, tid);
            const promptCost    = costFor(promptCap, tid);
            const frameCost     = costFor(frameCap, tid);
            // Video cost uses a tier-aware picker: prefer the admin's
            // explicit `tiers[tid]`, fall back to cost-based slotting so
            // Economy tier shows the cheapest model's cost (and Premium
            // shows the priciest) — matching what the runtime resolver
            // will actually use. See bug Feb 2026 where Economy showed
            // the Premium refs-to-video model's price.
            const videoModelForTier = videoCap
                ? (pickTierModelStrict(videoCap, tid) || pickTierModelByCost(videoCap, tid))
                : null;
            const videoCost = (() => {
                if (!videoCap || !videoModelForTier) {
                    return { value: TIER_FALLBACK[tid], trusted: false };
                }
                const value = parseCreditCost(videoModelForTier, TIER_FALLBACK[tid]);
                const trusted = Number.isFinite(videoModelForTier.cost_credits)
                    || Number.isFinite(videoModelForTier.credits_per_call);
                return { value, trusted };
            })();

            // Branding & rendering use flat platform credits — same for all tiers.
            // These are platform fees (not LLM model calls), so they're always trusted.
            const logoLine   = { value: logoEnabled ? 5 : 0,  trusted: true };
            const outroLine  = { value: outroEnabled ? 5 : 0, trusted: true };
            // Render is charged per video clip on the timeline (see RenderModal).
            const renderLine = { value: RENDER_COST_PER_CLIP, trusted: true };

            const lineItems = [
                { id: 'portraits', label: `Character portraits (${pending.portraits})`, count: pending.portraits, unit: portraitCost.value, trusted: portraitCost.trusted, icon: Users, capModel: pickTierModel(portraitCap, tid) },
                { id: 'costumes',  label: `Costume designs (${pending.costumes})`,     count: pending.costumes,  unit: costumeCost.value,  trusted: costumeCost.trusted,  icon: Shirt, capModel: pickTierModel(costumeCap, tid) },
                { id: 'elements',  label: `Element images (${pending.elements})`,      count: pending.elements,  unit: elementCost.value,  trusted: elementCost.trusted,  icon: Box,   capModel: pickTierModel(elementCap, tid) },
                { id: 'tryon',     label: `Virtual try-on (≤${pending.tryOnCandidates})`, count: pending.tryOnCandidates, unit: tryOnCost.value, trusted: tryOnCost.trusted, icon: Layers, capModel: pickTierModel(tryOnCap, tid) },
                { id: 'logo',      label: 'Logo overlay', count: logoEnabled ? 1 : 0,   unit: logoLine.value,   trusted: logoLine.trusted,   icon: ImageIcon, capModel: null },
                { id: 'outro',     label: 'Outro card',   count: outroEnabled ? 1 : 0,  unit: outroLine.value,  trusted: outroLine.trusted,  icon: ImageIcon, capModel: null },
                { id: 'storyboard',label: `Storyboard (${plannedScenes} scenes)`, count: pending.storyboardPending ? 1 : 0, unit: storyboardCost.value, trusted: storyboardCost.trusted, icon: FilmIcon, capModel: pickTierModel(storyboardCap, tid) },
                { id: 'prompts',   label: `Frame prompts (${plannedShots * 2})`,  count: plannedShots * 2, unit: promptCost.value, trusted: promptCost.trusted, icon: Wand2,    capModel: pickTierModel(promptCap, tid) },
                { id: 'frames',    label: `Frame images (${plannedShots * 2})`,   count: plannedShots * 2, unit: frameCost.value,  trusted: frameCost.trusted,  icon: ImageIcon, capModel: pickTierModel(frameCap, tid) },
                { id: 'videos',    label: `Video shots (${plannedShots})`,        count: plannedShots,     unit: videoCost.value,  trusted: videoCost.trusted,  icon: FilmIcon,  capModel: videoModelForTier },
                { id: 'render',    label: `Final render (${plannedShots} clips)`, count: plannedShots, unit: renderLine.value, trusted: renderLine.trusted, icon: Scissors, capModel: null },
            ];
            const total = lineItems.reduce((sum, l) => sum + l.count * l.unit, 0);
            // A tier is "fully trusted" only if every line item that's actually
            // contributing to the cost (count > 0) has a structured cost.
            const anyEstimated = lineItems.some(l => l.count > 0 && !l.trusted);

            // Distinct list of tiered models that will actually be used.
            // For non-video caps we keep the strict "must have an explicit
            // tier mapping" filter. For the video cap we include
            // `videoModelForTier` (which may come from cost-based fallback)
            // so the "Models used at this tier" panel surfaces the model
            // the runtime will actually call — fixing the bug where Economy
            // tier showed only `Veo3.1-lite-img2video` while the runtime
            // (for panel projects) actually called a refs-to-video model.
            const nonVideoCaps = [
                portraitCap, costumeCap, elementCap, tryOnCap, storyboardCap, promptCap, frameCap,
            ].filter(Boolean).filter(c => Array.isArray(c.tiers?.[tid]) && c.tiers[tid].length > 0);
            const nonVideoSummary = nonVideoCaps.map(c => ({
                capabilityName: c.name || c.key,
                model: pickTierModel(c, tid),
            })).filter(m => m.model);
            const videoSummary = (videoCap && videoModelForTier)
                ? [{ capabilityName: videoCap.name || videoCap.key, model: videoModelForTier }]
                : [];
            const modelSummary = [...nonVideoSummary, ...videoSummary];

            out[tid] = { lineItems, total, modelSummary, anyEstimated };
        }
        return out;
    }, [capabilities, pending, plannedShots, plannedScenes, logoEnabled, outroEnabled, videoNeedles]);

    const creditsUsedSoFar = Number(initialData.creditsUsed || 0);

    // ---------- Build selectedModels for a tier ----------
    // The tier selection is AUTHORITATIVE for video capabilities — when the
    // user picks Economy, they expect Economy refs-to-video / image-to-video
    // models, NOT a stale premium model that happened to be in localStorage
    // from a prior session.
    //
    // Strategy:
    //   1. Start from existing localStorage so non-video capabilities are
    //      preserved when the tier has no explicit mapping for them.
    //   2. For each capability with a `tiers[tierId]` entry → write it.
    //   3. For VIDEO capabilities specifically (refs-to-video,
    //      image-to-video, start-end-frame, text-to-video) → always force a
    //      tier-appropriate selection, falling back to cost-based slotting
    //      when the admin hasn't explicitly mapped tiers. This guarantees
    //      Economy ≠ Premium for the most price-sensitive line item.
    //   4. Any capability with no tier mapping AND no cost data is left
    //      alone (prior selection preserved).
    const VIDEO_CAP_KEYWORDS = [
        'references-to-video', 'references_to_video', 'references to video', 'referencestovideo',
        'image to video', 'image_to_video',
        'start-end-frame', 'start_end_frame', 'start end frame',
        'text to video', 'text_to_video',
    ];
    const isVideoCapability = (cap) => {
        const k = (cap?.key || '').toLowerCase();
        const n = (cap?.name || '').toLowerCase();
        return VIDEO_CAP_KEYWORDS.some(needle => k.includes(needle) || n.includes(needle));
    };

    const buildSelectedModelsForTier = useCallback((tierId) => {
        // Seed from any existing localStorage selection so non-tiered, non-
        // video capabilities keep their prior value.
        let out = {};
        try {
            const prior = JSON.parse(localStorage.getItem('selectedAIModels') || '{}');
            if (prior && typeof prior === 'object') out = { ...prior };
        } catch { /* noop */ }

        const missingTierMappings = [];
        const costFallbacksUsed = [];
        const priorityFallbacksUsed = [];
        for (const cap of capabilities) {
            if (!cap?.key) continue;
            const explicit = pickTierModelStrict(cap, tierId);
            if (explicit) {
                out[cap.key] = explicit.key || explicit.id;
                continue;
            }
            // No explicit tier mapping. For video capabilities, force a
            // cost-based pick so Economy/Premium are honoured even when
            // admin hasn't onboarded tier-specific entries.
            if (isVideoCapability(cap)) {
                const byCost = pickTierModelByCost(cap, tierId);
                if (byCost) {
                    out[cap.key] = byCost.key || byCost.id;
                    costFallbacksUsed.push({ key: cap.key, picked: byCost.key || byCost.id });
                    continue;
                }
            }
            // Feb 13 2026 — Non-video capability with no tier mapping.
            // Previously we skipped and warned, which left downstream
            // callers to hit "No selected model found for image generation".
            // Instead, honour the admin catalog's priority order:
            // capability.models is expected to be sorted so index 0 is the
            // priority pick. If a model.priority field exists we sort by
            // that first (ascending: 1 = top priority). This preserves the
            // user's tier choice for non-video caps while never leaving
            // the selection unset.
            const priorityPicked = pickTierModelByPriority(cap);
            if (priorityPicked) {
                out[cap.key] = priorityPicked.key || priorityPicked.id;
                priorityFallbacksUsed.push({ key: cap.key, picked: priorityPicked.key || priorityPicked.id });
                continue;
            }
            // Absolute last resort — nothing in models[] either. Just log.
            missingTierMappings.push(cap.key);
        }

        if (costFallbacksUsed.length > 0) {
            console.info(
                `ℹ️ Tier "${tierId}" used cost-based fallback for video capabilities:`,
                costFallbacksUsed,
                '— admin catalog has no explicit tier mapping for these.'
            );
        }
        if (priorityFallbacksUsed.length > 0) {
            console.info(
                `ℹ️ Tier "${tierId}" used priority-order fallback (capability.models[0]) for:`,
                priorityFallbacksUsed,
                '— admin catalog has no explicit tier mapping for these; picked the top-priority model in each capability.'
            );
        }
        if (missingTierMappings.length > 0) {
            console.warn(
                `⚠️ Tier "${tierId}" has no models AT ALL for capabilities:`,
                missingTierMappings,
                '— neither tier nor models[] populated. Admin catalog gap.'
            );
        }
        try { localStorage.setItem('selectedAIModels', JSON.stringify(out)); } catch { /* noop */ }
        // ALSO persist the tier itself so downstream code paths (notably
        // `aiService.generateVideo`'s fallback chain) can cap the fallback
        // at-or-below the user's tier. Without this, a transient failure on
        // the Economy model silently escalates the request to Standard and
        // then Premium — billing surprise + ignored tier selection (see bug
        // Feb 28, 2026: Economy + refs-to-video → ended up calling
        // seedance2-ref2video). Read back via `getSelectedTier()` in
        // modelHelper.js.
        try { localStorage.setItem('selectedAITier', tierId); } catch { /* noop */ }
        try { window.dispatchEvent(new CustomEvent('ai-models-changed', { detail: out })); } catch { /* noop */ }
        return out;
    }, [capabilities]);

    // ---------- Generate Storyboard / Auto-Pilot click ----------
    // mode: 'storyboard' (default — drop user on the Storyboard editor afterwards)
    //       'autopilot'  (continue running every remaining stage automatically)
    const handleGenerate = async (mode = 'storyboard') => {
        if (running) return;
        setError(null); setRunning(true); setStage('gen'); setProgress(null);
        // Feb 2026 — Tier logic fully retired. The user's AI model picks
        // come exclusively from the AI Toolkit page (persisted to
        // `localStorage.selectedAIModels` by AIToolkitSelector). We read
        // them here as-is without overriding — no tier cap, no cost-based
        // swap. This eliminates the "toolkit-selected model was silently
        // replaced by tier default" conflict.
        let selectedModels = {};
        try {
            const stored = JSON.parse(localStorage.getItem('selectedAIModels') || '{}');
            if (stored && typeof stored === 'object') selectedModels = stored;
        } catch { /* noop */ }
        const characters = initialData.characters || [];
        try {
            // Phase A: portraits + costumes + try-on
            const askTryOn = (cands) => new Promise((resolve) => {
                setTryOnPending({ characters: cands, resolve });
            });
            const updatedChars = await runPreStoryboardPipeline({
                project: initialData,
                characters,
                askTryOn,
                onProgress: setProgress,
                onSaveMedia,
                onTrackCreditsUsed,
                onRefreshCredits,
            });
            setProgress(null);
            setTryOnPending(null);

            // Phase B: storyboard generation (only if not already there)
            let scenes = initialData.scenes;
            if (!scenes || scenes.length === 0) {
                if ((initialData.script || '').trim()) {
                    setStage('storyboard');
                    const sbResult = await generateStoryboard({ ...initialData, characters: updatedChars }, () => {});
                    if (sbResult?.credits_used && onTrackCreditsUsed) onTrackCreditsUsed(sbResult.credits_used);
                    const sceneArr = Array.isArray(sbResult?.scenes || sbResult) ? (sbResult?.scenes || sbResult) : [];
                    scenes = sceneArr.map((sData, sIdx) => ({
                        id: `scene-${sIdx}-${Date.now()}`,
                        shots: (sData.shots || []).map((shData, shIdx) => {
                            const { characterIds, elementIds } = mapEntitiesToShot(shData, updatedChars);
                            const rawDialogues = Array.isArray(shData.dialogues) ? shData.dialogues
                                : Array.isArray(shData.dialogue) ? shData.dialogue : [];
                            return {
                                id: `shot-${sIdx}-${shIdx}-${Date.now()}`,
                                description: shData.description,
                                startFramePrompt: shData.startFramePrompt,
                                endFramePrompt: shData.endFramePrompt,
                                narration: shData.narration,
                                characterIds,
                                elementIds,
                                dialogues: rawDialogues.map((d, dIdx) => {
                                    const cn = (d.characterName || d.character_name || d.character || '').toLowerCase();
                                    const matched = updatedChars.find(c => {
                                        const cName = (c.name || '').toLowerCase();
                                        if (cName === cn) return true;
                                        const f1 = cName.split(/\s+/)[0];
                                        const f2 = cn.split(/\s+/)[0];
                                        return f1.length > 2 && (cName.includes(cn) || cn.includes(f1) || f1 === f2);
                                    });
                                    return {
                                        characterId: matched?.id,
                                        characterName: d.characterName || d.character_name || d.character || '',
                                        text: d.text || d.dialogue || '',
                                        order: d.order ?? dIdx,
                                        startTime: d.startTime || d.start_time,
                                        endTime: d.endTime || d.end_time,
                                    };
                                }).filter(d => d.characterId && d.text),
                            };
                        }),
                    }));
                } else {
                    // No script — give a single blank scene so storyboard renders.
                    scenes = [{
                        id: `scene-${Date.now()}`,
                        shots: [{ id: `shot-${Date.now()}`, description: 'New shot', startFramePrompt: '', endFramePrompt: '', narration: '', characterIds: [], elementIds: [] }],
                    }];
                }
            }

            onSubmit({ characters: updatedChars, scenes, selectedModels, autoPilot: mode === 'autopilot' });
        } catch (e) {
            console.error('TierSelection generation pipeline failed:', e);
            setError(e.message || 'Generation failed. Please try again.');
            setRunning(false); setStage('idle'); setProgress(null); setTryOnPending(null);
        }
    };

    const stageLabel = stage === 'gen' ? 'Generating assets…' : stage === 'storyboard' ? 'Generating storyboard…' : '';
    const stepLabel = progress?.step === 'portrait' ? 'Portrait'
        : progress?.step === 'costume' ? 'Costume'
        : progress?.step === 'try-on' ? 'Try-on'
        : 'Working';

    return (
        <div className="h-full overflow-y-auto" data-testid="tier-page-scroll">
            <div className="max-w-6xl mx-auto py-8 px-4">
            <div className="bg-zinc-950/80 border border-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl">
                <div className="flex items-start gap-3 mb-2">
                    <div className="w-12 h-12 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-violet-300" />
                    </div>
                    <div className="flex-1">
                        <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Ready to generate</h2>
                        <p className="text-sm text-zinc-400 mt-1">Your AI models are already picked from the AI Toolkit. Choose how hands-on you want to be — review shots first, or let auto-pilot run the whole thing.</p>
                    </div>
                </div>

                {error && (
                    <div className="mb-4 mt-4 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-xs" data-testid="tier-error">
                        {error}
                    </div>
                )}

                {/* Feb 2026 — Removed the "Choose a tier" step + estimated
                    credit displays. Users pick their AI models up-front via
                    the AI Toolkit page (Step 2 of the wizard) so the tier
                    picker here was redundant and confusing. `selectedTier`
                    still defaults to `standard` for downstream pipeline
                    compatibility. */}
                <div className="flex items-center gap-2 mt-6 mb-3">
                    <div className="w-6 h-6 rounded-full bg-violet-500/20 border border-violet-500/40 flex items-center justify-center text-[11px] font-bold text-violet-300">1</div>
                    <div className="text-sm font-semibold text-zinc-200">Pick how hands-on you want to be</div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 pb-4">
                    <button
                        type="button"
                        onClick={() => handleGenerate('storyboard')}
                        disabled={running || loadingCaps}
                        className="text-left p-4 rounded-xl border border-violet-500/40 bg-violet-500/10 hover:bg-violet-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        data-testid="tier-generate-btn"
                    >
                        <div className="text-base font-semibold text-violet-100">Generate Storyboard</div>
                        <div className="text-sm text-zinc-300 mt-1 leading-relaxed">I'd like to review and tweak the shots before videos are generated.</div>
                        <div className="flex items-center gap-1.5 mt-3 text-sm text-violet-200 font-semibold">
                            {running
                                ? (<><Loader2 className="w-4 h-4 animate-spin" /> {stageLabel || 'Working…'}</>)
                                : (<><Wand2 className="w-4 h-4" /> Take me to the storyboard</>)
                            }
                        </div>
                    </button>
                    <button
                        type="button"
                        onClick={() => handleGenerate('autopilot')}
                        disabled={running || loadingCaps}
                        className="relative text-left p-4 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-violet-600/30"
                        data-testid="tier-generate-video-btn"
                    >
                        <span className="absolute top-2.5 right-2.5 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-white/15 border border-white/20 text-white/90 font-bold">
                            Recommended
                        </span>
                        <div className="text-base font-semibold text-white">Generate the Video</div>
                        <div className="text-sm text-violet-100 mt-1 leading-relaxed">Run everything end-to-end automatically. Sit back, get an mp4 in minutes.</div>
                        <div className="flex items-center gap-1.5 mt-3 text-sm text-white font-semibold">
                            {running
                                ? (<><Loader2 className="w-4 h-4 animate-spin" /> {stageLabel || 'Working…'}</>)
                                : (<><Wand2 className="w-4 h-4" /> Start auto-pilot</>)
                            }
                        </div>
                    </button>
                </div>

                <div className="flex items-center justify-between gap-3 pt-3 border-t border-white/10">
                    <button
                        type="button"
                        onClick={onBack}
                        disabled={running}
                        className="px-4 py-2 rounded-lg border border-white/15 text-sm text-zinc-300 hover:bg-white/5 transition-all flex items-center gap-2 disabled:opacity-40"
                        data-testid="tier-back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </button>
                </div>
            </div>

            {/* ---------- Progress overlay (while running) ---------- */}
            {running && progress && (
                <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 pointer-events-none">
                    <div className="bg-zinc-950 border border-white/10 rounded-2xl p-6 max-w-md w-full pointer-events-auto" data-testid="tier-progress-overlay">
                        <div className="flex items-center gap-3 mb-3">
                            <Loader2 className="w-5 h-5 text-violet-400 animate-spin" />
                            <div>
                                <div className="text-sm font-semibold text-white">{stepLabel}: {progress.name}</div>
                                <div className="text-xs text-zinc-400">{progress.current} / {progress.total}</div>
                            </div>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                            <div
                                className="h-full bg-violet-500 transition-all"
                                style={{ width: progress.total > 0 ? `${(progress.current / progress.total) * 100}%` : '0%' }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* ---------- Try-on confirmation modal ---------- */}
            {tryOnPending && (
                <TryOnConfirmModal
                    characters={tryOnPending.characters}
                    onConfirm={(ids) => { tryOnPending.resolve(ids); setTryOnPending(null); }}
                    onSkip={() => { tryOnPending.resolve([]); setTryOnPending(null); }}
                />
            )}
            </div>
        </div>
    );
};

export default TierSelection;
